import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from '@mui/styled-engine-sc'
import {
  Box,
  Button,
  Divider,
  Drawer,
  AppBar as MuiAppBar,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { Close as CloseIcon, Menu as MenuIcon } from '@mui/icons-material'
import { THEMES } from '../../constants'
import { useUserManual } from '../../contexts/userManual'
import { Announcement } from '../../types'
import { getAnnouncements } from '../../services/announcementService'
import useClosedAnnouncements from '../../hooks/useClosedAnnouncements'
import NavbarNotificationsDropdown from './NavbarNotificationsDropdown'
import NavbarMessagesDropdown from './NavbarMessagesDropdown'
import NavbarProfile from './NavbarProfile'
import InstructionForNewUser from '../NewUserInstructions'
import ConfluencePageArticle from '../ConfluencePageArticle'
import AnnouncementAlert from '../AnnouncementAlert'
import { useAuth } from '../../hooks'
import ConfluencePageLinkButton from '../ConfluencePageLinkButton'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux'

const AppBar = styled(MuiAppBar)`
  background: ${(props) => props.theme.header.background};
  color: ${(props) => props.theme.header.color};
`

type NavbarProps = {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>
}

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle }) => {
  /**
   * The translation function.
   */
  const [t] = useTranslation('common')

  /**
   * The theme.
   */
  const theme = localStorage.getItem('theme')

  /**
   * The user manual.
   */
  const userManual = useUserManual()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The announcements.
   */
  const [announcements, setAnnouncements] = useState<Announcement[]>([])

  /**
   * The current announcements
   */
  const [currentAnnouncements, setCurrentAnnouncements] = useState<Announcement[]>([])

  /**
   * Indicates if the page toolbar is fixed.
   */
  const { isFixed: pageToolbarIsFixed } = useSelector(
    (state: RootState) => state.components.PageToolbar
  )

  /**
   * Indicates if the announcements are loading.
   */
  const [isLoading, setIsLoading] = useState<boolean>(true)

  /**
   * Indicates if the user manual is open.
   */
  const [isUserManualOpen, setIsUserManualOpen] = useState<boolean>(false)

  useEffect(() => {
    loadAnnouncements()
  }, [])

  /**
   * Get the closed announcements.
   */
  const closedAnnouncements = useClosedAnnouncements()

  async function loadAnnouncements() {
    try {
      setIsLoading(true)

      const currentAnnouncements = await getAnnouncements({
        currentTime: new Date()
      })

      setCurrentAnnouncements(currentAnnouncements)

      const unreadAnnouncements = currentAnnouncements.filter(
        (announcement: Announcement) => !closedAnnouncements.includes(announcement.id)
      )

      setAnnouncements(unreadAnnouncements)
    } finally {
      setIsLoading(false)
    }
  }

  function handleCloseAnnouncement(id: string) {
    if (currentUser && !closedAnnouncements.includes(id)) {
      localStorage.setItem(
        'closedAnnouncements',
        JSON.stringify({
          [currentUser.id]: [...closedAnnouncements, id]
        })
      )

      const unreadAnnouncements = announcements.filter(
        (announcement: Announcement) => announcement.id !== id
      )

      setAnnouncements(unreadAnnouncements)
    }
  }

  /**
   * Handle theme change
   */
  function _handleColorChange(): void {
    if (theme === THEMES.LIGHT) {
      localStorage.setItem('theme', THEMES.DARK)
      window.location.reload()
    } else {
      localStorage.setItem('theme', THEMES.LIGHT)
      window.location.reload()
    }
  }

  /**
   * Open the user manual.
   */
  function openUserManual(): void {
    setIsUserManualOpen(true)
  }

  /**
   * Close the user manual.
   */
  function closeUserManual(): void {
    setIsUserManualOpen(false)
  }

  /**
   * Get the width of the scroll bar.
   *
   * @see https://stackoverflow.com/a/13382873
   */
  function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll' // forcing scrollbar to appear
    // outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps

    document.body.appendChild(outer)

    // Creating inner element and placing it in the container
    const inner = document.createElement('div')
    outer.appendChild(inner)

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    if (outer.parentNode !== null) {
      // Removing temporary elements from the DOM
      outer.parentNode.removeChild(outer)
    }

    return scrollbarWidth
  }

  return (
    <React.Fragment>
      <InstructionForNewUser />
      <AppBar position="sticky" elevation={0} sx={{ boxShadow: 1 }}>
        <Toolbar>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={3}
            width="100%"
          >
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton color="inherit" size="large" onClick={onDrawerToggle}>
                <MenuIcon />
              </IconButton>
            </Box>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              width="100%"
            >
              {userManual !== null && (
                <>
                  <Tooltip title={t('userManual') as string}>
                    <IconButton size="large" color="inherit" onClick={() => openUserManual()}>
                      <QuestionMarkIcon />
                    </IconButton>
                  </Tooltip>

                  <Drawer
                    anchor="right"
                    open={isUserManualOpen}
                    onClose={() => closeUserManual()}
                    PaperProps={{
                      sx: {
                        maxWidth: '525px',
                        width: '100%',
                        py: 6,
                        px: 3
                      }
                    }}
                  >
                    <Box>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="column" gap={0}>
                          <Typography variant="h3">{userManual.title}</Typography>
                          <Typography
                            color={
                              theme !== null && theme === THEMES.DARK
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.5)'
                            }
                          >
                            {t('userManual')}
                          </Typography>
                        </Stack>

                        <IconButton
                          onClick={() => closeUserManual()}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>

                      <Divider sx={{ mt: 6 }} />

                      {userManual && <ConfluencePageLinkButton page={userManual} sx={{ mt: 6 }} />}

                      <ConfluencePageArticle page={userManual} />

                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => closeUserManual()}
                          sx={{ mt: 6 }}
                        >
                          {t('closeUserManual')}
                        </Button>
                      </Box>
                    </Box>
                  </Drawer>
                </>
              )}

              {/*<Tooltip title={t('Navbar.help') as string}>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => dispatch(setShowInstructions(true))}
                >
                  <QuestionMarkIcon />
                </IconButton>
              </Tooltip>*/}

              <NavbarMessagesDropdown announcements={currentAnnouncements} />
              <NavbarNotificationsDropdown />

              {/*<Tooltip title={t('Navbar.switchTheme') as string}>
                <IconButton
                  sx={{ ml: 1 }}
                  onClick={handleColorChange}
                  color="inherit"
                >
                  {theme === THEMES.DARK ? (
                    <Brightness7Icon />
                  ) : (
                    <Brightness4Icon />
                  )}
                </IconButton>
              </Tooltip>*/}

              {/*<NavbarLanguagesDropdown />*/}
              <NavbarProfile />
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      {!isLoading && announcements.length > 0 && (
        <Stack
          direction="column"
          gap={3}
          padding={3}
          sx={{
            zIndex: 1200,
            position: 'relative',
            top: pageToolbarIsFixed ? 64 : 0,
            mr: `${getScrollbarWidth()}px`
          }}
        >
          {announcements.map((announcement) => (
            <AnnouncementAlert
              key={announcement.id}
              announcement={announcement}
              onClose={() => handleCloseAnnouncement(announcement.id)}
            />
          ))}
        </Stack>
      )}
    </React.Fragment>
  )
}

export default Navbar
